import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastError } from '../../utils/alertHelper';

const UserView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('business');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`/api/user/${id}`);
        const u = res.data.user;
        if (!u) throw new Error('User not found');
        setUser(u);
      } catch (err) {
        toastError('Failed to fetch user');
        navigate('/user');
      }
    };
    fetchUser();
  }, [id, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return isNaN(date) ? '-' : date.toLocaleDateString();
  };

  const renderData = (data) => {
    if (!data) return '-';
    if (Array.isArray(data)) {
      if (data.length === 0) return '-';
      return data
        .map((item) => {
          if (!item) return '-';
          if (typeof item === 'string') return item;
          if (typeof item === 'object') {
            return item.name || item.label || item._id || JSON.stringify(item);
          }
          return String(item);
        })
        .join(', ');
    }
    if (typeof data === 'object') {
      return data.name || data.label || data._id || JSON.stringify(data);
    }
    return String(data);
  };

  if (!user) return <div className="text-center py-5">Loading user data...</div>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="text-dark border-bottom pb-2 mb-0">View User Details</h4>
        <Link
          to={`/user/edit/${user._id}`}
          className="btn btn-sm"
          style={{ backgroundColor: '#F36B24', color: '#fff' }}
        >
          <span style={{ fontSize: '15px', verticalAlign: 'middle' }}>Edit</span>
        </Link>
      </div>
      <div className="row">
        {/* LEFT SIDE - Basic Info */}
        <div className="col-md-4">
          <div className="card shadow p-3">
            <h5 className="text-primary mb-3">Basic Information</h5>
            <p><strong>Full Name:</strong> {`${user.firstName || '-'} ${user.middleName || ''} ${user.lastName || ''}`}</p>
            <p><strong>Email:</strong> {user.email || '-'}</p>
            <p><strong>Mobile:</strong> {user.mobileNumber || '-'}</p>
            <p><strong>Birthdate:</strong> {formatDate(user.dob)}</p>
            <p><strong>Anniversary:</strong> {formatDate(user.anniversaryDate)}</p>
            <p><strong>Blood Group:</strong> {user.bloodGroup || '-'}</p>
            <p><strong>Gender:</strong> {user.gender || '-'}</p>
            <p><strong>Address:</strong> {
              [user.address, user.city?.name, user.state?.name, user.country?.name]
              .filter(Boolean)
              .join(', ') || '-'
            }</p>
            <p><strong>Emergency Contact:</strong> {user.emergencyContactName || '-'} ({user.emergencyContactMobile || '-'})</p>
            <p><strong>Roles:</strong> {renderData(user.roles)}</p>
            <p><strong>Clusters:</strong> {renderData(user.cluster)}</p>
            <p><strong>Chapters:</strong> {renderData(user.chapter)}</p>
            <p><strong>Responsibilities:</strong> {renderData(user.responsibilities)}</p>
          </div>
        </div>

        {/* RIGHT SIDE - Tabs */}
        <div className="col-md-8">
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'business' ? 'active' : ''}`}
                onClick={() => setActiveTab('business')}
              >
                Business Info
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'children' ? 'active' : ''}`}
                onClick={() => setActiveTab('children')}
              >
                Child Info
              </button>
            </li>
          </ul>

          {/* Business Tab */}
          {activeTab === 'business' && (
            <div className="card shadow p-3">
              <h5 className="text-success mb-3">Business Information</h5>
              {user.business && user.business.length > 0 ? (
                user.business.map((biz, idx) => (
                  <div key={idx} className="mb-4">
                    <p><strong>Business Name:</strong> {biz.name || '-'}</p>
                    <p><strong>Established:</strong> {formatDate(biz.establishedDate)}</p>
                    <p><strong>Mobile:</strong> {biz.number || '-'}</p>
                    <p><strong>Type:</strong> {renderData(biz.type)}</p>
                    <p><strong>Category:</strong> {renderData(biz.category)}</p>
                    <p><strong>Sub Category:</strong> {renderData(biz.subCategory)}</p>
                    <p><strong>Formation:</strong> {renderData(biz.formation)}</p>
                    <p><strong>Products/Services:</strong> {renderData(biz.productsOrServices)}</p>
                    <p><strong>Team Size:</strong> {biz.teamSize || '-'}</p>
                    <p><strong>GST Number:</strong> {biz.gstNumber || '-'}</p>
                    <p><strong>Website:</strong> {biz.websiteURL || '-'}</p>
                    <p><strong>Address:</strong> {biz.address || '-'}</p>
                    <p><strong>Description:</strong> {biz.description || '-'}</p>
                    <hr />
                  </div>
                ))
              ) : (
                <p>No business data available.</p>
              )}
            </div>
          )}

          {/* Children Tab */}
          {activeTab === 'children' && (
            <div>
              {/* Wife Info */}
              <div className="card shadow p-3 mb-4">
                <h5 className="text-info mb-3">Wife Information</h5>
                {user?.wife ? (
                  <>
                    <p><strong>Name:</strong> {user.wife.name || '-'}</p>
                    <p><strong>DOB:</strong> {user.wife.dob ? formatDate(user.wife.dob) : '-'}</p>
                    <p><strong>Mobile Number:</strong> {user.wife.mobileNumber || '-'}</p>
                  </>
                ) : (
                  <p>No wife data available.</p>
                )}
              </div>

              {/* Children Info */}
              <div className="card shadow p-3">
                <h5 className="text-info mb-3">Children</h5>
                {user?.children && user.children.length > 0 ? (
                  user.children.map((child, idx) => (
                    <div key={idx} className="mb-2">
                      <p><strong>Name:</strong> {child.name || '-'}</p>
                      <p><strong>DOB:</strong> {child.dob ? formatDate(child.dob) : '-'}</p>
                      <p><strong>Gender:</strong> {child.gender || '-'}</p>
                      <hr />
                    </div>
                  ))
                ) : (
                  <p>No child data available.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserView;
